import React, { useEffect, useRef, useState } from "react";

/**
 * Polled JPEG snapshot tile, used for the camera grid in CamerasPage.
 *
 * Why not <HlsVideo>: 8 simultaneous hls.js instances on one origin each
 * spin up their own go2rtc HLS session; go2rtc keeps only ~2 segments of
 * buffer (~1s) per session, so when N sessions race for segment.ts?n=0,
 * many of them lose and end up in a stall/recover loop — the user sees
 * one tile pop up and the rest stay black. Snapshots sidestep all of
 * this: one HTTP fetch per tile per ~1.5 s, no codec state, no session.
 *
 * Why blob URLs: <img src=...> can't carry custom request headers, but
 * /snapshot/ on the relay sits behind Cloudflare Access which requires
 * the CF-Access-Client-Id/Secret pair. We fetch via XHR-with-headers
 * and hand the resulting object URL to the <img>. Same trick the
 * recordings playback uses for MP4 blobs.
 */
export default function SnapshotImg({
  src,           // base URL — _b=<bust> appended automatically
  alt,
  className,
  style,
  authHeaders,   // { 'CF-Access-Client-Id': ..., 'CF-Access-Client-Secret': ... } — pass a stable object
  intervalMs = 1500,
  onFrameSize,   // optional (width, height) of the camera frame, reported when a snapshot loads
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const blobUrlRef = useRef(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const hiddenRef = useRef(typeof document !== "undefined" && document.hidden);

  useEffect(() => {
    cancelledRef.current = false;

    const onVis = () => {
      hiddenRef.current = document.hidden;
      if (!document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVis);

    const tick = async () => {
      if (cancelledRef.current) return;
      if (hiddenRef.current) {
        timerRef.current = window.setTimeout(tick, intervalMs);
        return;
      }
      try {
        const sep = src.includes("?") ? "&" : "?";
        const url = `${src}${sep}_b=${Date.now()}`;
        const r = await fetch(url, { headers: authHeaders || {} });
        if (!r.ok) throw new Error(`snapshot ${r.status}`);
        const blob = await r.blob();
        if (cancelledRef.current) return;
        const obj = URL.createObjectURL(blob);
        const prev = blobUrlRef.current;
        blobUrlRef.current = obj;
        setBlobUrl(obj);
        if (prev) URL.revokeObjectURL(prev);
        timerRef.current = window.setTimeout(tick, intervalMs);
      } catch (_e) {
        if (cancelledRef.current) return;
        timerRef.current = window.setTimeout(tick, intervalMs * 2);
      }
    };

    tick();

    return () => {
      cancelledRef.current = true;
      document.removeEventListener("visibilitychange", onVis);
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [src, intervalMs, authHeaders]);

  if (!blobUrl) {
    return <div className={className} style={style} aria-label={alt} />;
  }
  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      style={style}
      onLoad={onFrameSize ? (e) => onFrameSize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight) : undefined}
    />
  );
}
